/**
 * Session cache for the small, slow-changing lists that back the filter bar
 * (LOBs, functions, branches).
 *
 * Why this exists: LobFilter / FunctionFilter / LocationFilter each fired their
 * own GET on every mount, and they mount on Dashboard, Time Logs, Reports and
 * Employees. Walking between those four pages cost ~12 requests for three lists
 * that had not changed — and because each filter renders nothing until its
 * response lands, the dropdowns visibly popped into the filter card a beat after
 * the rest of it. The lists themselves are tiny (a handful of rows); the delay
 * was entirely round-trip and queueing behind the page's own analytics calls.
 *
 * Two guarantees:
 *   - `peek` is synchronous, so a cached list is already in the component's
 *     first render — no flicker at all after the first visit.
 *   - concurrent callers for the same key share ONE in-flight request, so two
 *     filters mounting together never race the same endpoint twice.
 *
 * The lists are scoped server-side to the signed-in user, so the cache MUST be
 * cleared on login/logout (AuthContext does this) — a portal login does not
 * reload the page, and a stale entry would leak the previous user's LOB names.
 */

const TTL_MS = 5 * 60 * 1000;

const entries = new Map(); // key -> { data, at }
const inflight = new Map(); // key -> Promise

function isFresh(entry) {
  return entry && Date.now() - entry.at < TTL_MS;
}

/** Cached value for `key`, or undefined. Synchronous — safe as useState seed. */
export function peekOptions(key) {
  const entry = entries.get(key);
  return isFresh(entry) ? entry.data : undefined;
}

/**
 * Resolve `key`, fetching via `fetcher` only on a miss. Rejections are not
 * cached, so a failed load retries on the next mount.
 */
export function loadOptions(key, fetcher) {
  const entry = entries.get(key);
  if (isFresh(entry)) return Promise.resolve(entry.data);

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = fetcher().then(
    (data) => {
      entries.set(key, { data, at: Date.now() });
      inflight.delete(key);
      return data;
    },
    (err) => {
      inflight.delete(key);
      throw err;
    }
  );
  inflight.set(key, promise);
  return promise;
}

/**
 * Drop every key starting with `prefix` (e.g. 'lobs' clears 'lobs' and
 * 'lobs:includeInactive'). Called from the api modules after any write, so a
 * newly created LOB shows up in the filters without a page reload.
 */
export function invalidateOptions(prefix) {
  for (const key of [...entries.keys()]) {
    if (key.startsWith(prefix)) entries.delete(key);
  }
  for (const key of [...inflight.keys()]) {
    if (key.startsWith(prefix)) inflight.delete(key);
  }
}

/** Wipe everything — on sign-in and sign-out, where the scope changes wholesale. */
export function clearOptionsCache() {
  entries.clear();
  inflight.clear();
}
